import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Shared fetch lifecycle for dashboard pages: run `fn` on mount (and whenever
 * its identity changes), optionally re-run it on an interval, and keep the
 * standard loading/error state. `fn` must be stable (useCallback) and does the
 * actual fetching + state updates; the hook owns error capture and the loading
 * flag. `enabled: false` mirrors a page's guard clause — nothing runs and
 * `loading` stays true. The returned `load` is the wrapped loader for
 * mutation paths that re-fetch after a write.
 */
export function useLoad(
  fn: () => Promise<void>,
  opts: { pollMs?: number; enabled?: boolean } = {},
) {
  const { pollMs, enabled = true } = opts;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const requestedId = ++requestId.current;
    if (!enabled) return;
    try {
      await fn();
      if (requestedId === requestId.current) setError(null);
    } catch (err) {
      if (requestedId === requestId.current) setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (requestedId === requestId.current) setLoading(false);
    }
  }, [fn, enabled]);

  useEffect(() => {
    void load();
    const t = pollMs ? setInterval(() => void load(), pollMs) : undefined;
    return () => {
      if (t) clearInterval(t);
      requestId.current += 1;
    };
  }, [load, pollMs]);

  return { loading, error, setError, load };
}
